import type {
  BooleanComparator,
  DateComparator,
  EnumComparator,
  Filter,
  FilterComparator,
  FilterDataType,
  NumericComparator,
  SetComparator,
  StringComparator,
} from "./FilterSchema";

/**
 * UI labels and a11y phrasing for the OperatorPicker pills.
 *
 * Tuple / type / predicate ownership lives in `FilterSchema`; this
 * module is the presentation layer. Keeping the map structurally
 * exhaustive (no `Partial<>`) means adding a new comparator to any
 * per-type tuple in `FilterSchema` fails to compile here until a label
 * is supplied.
 *
 * The same wire comparator can read differently depending on the field
 * type — `EQUALS` is "equals" for strings, "=" for numbers, "is" for
 * enums — so labels are keyed on `(FilterDataType, comparator)`.
 */
// Numeric glyph labels, shared by the `numeric` and `metric` types (a metric
// renders identically to a numeric filter — only `Filter["type"]` differs).
const numericLabels: Record<NumericComparator, string> = {
  EQUALS: "=",
  NOT_EQUALS: "≠",
  GREATER_THAN: ">",
  LESS_THAN: "<",
  GREATER_THAN_OR_EQUAL: "≥",
  LESS_THAN_OR_EQUAL: "≤",
};

const COMPARATOR_LABELS: {
  string: Record<StringComparator, string>;
  numeric: Record<NumericComparator, string>;
  date: Record<DateComparator, string>;
  boolean: Record<BooleanComparator, string>;
  set: Record<SetComparator, string>;
  enum: Record<EnumComparator, string>;
  metric: Record<NumericComparator, string>;
} = {
  string: {
    EQUALS: "equals",
    NOT_EQUALS: "not equals",
    CONTAINS: "contains",
    NOT_CONTAINS: "not contains",
    LIKE: "like",
    NOT_LIKE: "not like",
  },
  numeric: numericLabels,
  metric: numericLabels,
  date: {
    LESS_THAN: "before",
    GREATER_THAN: "after",
    BETWEEN: "between",
    TODAY: "today",
    LAST_7_DAYS: "last 7 days",
    LAST_30_DAYS: "last 30 days",
    LAST_90_DAYS: "last 90 days",
    THIS_MONTH: "this month",
  },
  boolean: {
    EQUALS: "equals",
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
  },
  set: {
    CONTAINS: "contains",
    NOT_CONTAINS: "not contains",
  },
  enum: {
    EQUALS: "is",
    NOT_EQUALS: "is not",
    IS_NOT_NULL: "is set",
    IS_NULL: "is not set",
  },
};

export function comparatorLabel(
  type: FilterDataType,
  comparator: FilterComparator,
): string {
  // The map is exhaustive per type at the type level, but the public
  // signature accepts the umbrella `FilterComparator` so the
  // OperatorPicker can pass through without narrowing. The cast at the
  // lookup site is safe because callers iterate `comparatorsByType[type]`
  // / `presetsByType[type]`, which are guaranteed-valid for the type.
  return (COMPARATOR_LABELS[type] as Record<string, string>)[comparator];
}

/**
 * Comparator label for an active-filter chip, accounting for cardinality.
 * A multi-value `enum` selection fans out to OR (`EQUALS`) or AND
 * (`NOT_EQUALS`) in `filterToCondition`, so the chip reads "is any of" /
 * "is none of" rather than the singular "is" / "is not". Every other case
 * — including single-value enums — delegates to `comparatorLabel`.
 *
 * Display-only: the wire `comparator` is still `EQUALS` / `NOT_EQUALS`, and
 * the OperatorPicker pills keep their singular labels (the picker commits a
 * comparator, not a value count, so it can't know the cardinality yet).
 */
export function comparatorLabelForFilter(filter: Filter): string {
  if (filter.type === "enum" && filter.values.length > 1) {
    if (filter.comparator === "EQUALS") {
      return "is any of";
    }
    if (filter.comparator === "NOT_EQUALS") {
      return "is none of";
    }
  }
  return comparatorLabel(filter.type, filter.comparator);
}

/**
 * Per-(type, comparator) spoken-label overrides. Most types' visual
 * labels are already phrases that read fine — only date diverges
 * (visual "before" for the `LESS_THAN` wire comparator, etc.). Numeric
 * is intentionally absent here: its glyph visuals can't be spoken, so
 * the fallback to the underscored comparator key ("LESS_THAN" →
 * "less than") is exactly right.
 */
const SPOKEN_LABEL_OVERRIDES: Partial<
  Record<FilterDataType, Partial<Record<FilterComparator, string>>>
> = {
  date: {
    LESS_THAN: "before",
    GREATER_THAN: "after",
  },
};

/**
 * Screen-reader-spoken label for a comparator within a field type.
 * Numeric pills render as glyphs ("=", "≠", "≤", …) which screen
 * readers narrate inconsistently across vendors — providing an explicit
 * phrase form gets us deterministic announcement. Date pills display
 * words ("before", "after") that don't match the wire comparator key,
 * so we override there too. Falls back to the comparator key with
 * underscores swapped for spaces ("GREATER_THAN_OR_EQUAL" → "greater
 * than or equal").
 */
export function spokenComparatorLabel(
  type: FilterDataType,
  comparator: FilterComparator,
): string {
  return (
    SPOKEN_LABEL_OVERRIDES[type]?.[comparator] ??
    comparator.toLowerCase().replace(/_/g, " ")
  );
}
